<script setup lang="ts" generic="T, C extends ColumnModel, R extends RecordMap<C>">
import { PV } from "@/widgets";
import { isNil } from "es-toolkit";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProfileStore } from "../../stores/profile.store";
import type { ColumnModel, RecordMap, TableColumn, defineTable } from "../common";
import { CustomTable } from "../internal";

const props = withDefaults(
    defineProps<{
        table: ReturnType<typeof defineTable<T, C, R>>;
        expandable?: boolean;
    }>(),
    {
        expandable: true,
    },
);

const slots = defineSlots<
    TableColumn<R> & {
        _tools: () => any;
        _buttons: () => any;
    }
>();

const route = useRoute();
const router = useRouter();
const profile = useProfileStore().data;

function qnum(name: string, defaultValue: number) {
    if (route.query[name] === undefined) {
        return defaultValue;
    }

    try {
        return parseInt(route.query[name] as string);
    } catch (_) {
        return defaultValue;
    }
}

const isExpanded = ref(qnum("expanded", 0) === 1);
const page = ref<number>(qnum("page", 0));
const rows = ref<number>(qnum("rows", 10));
const search = ref<string | undefined>(route.query["search"] as string | undefined);

const tableCols = computed(() => {
    const kind = profile?.kind;

    let keys: Set<keyof R>;
    if (isNil(kind)) {
        keys = new Set([]);
    } else {
        keys = new Set(props.table.visible[kind] ?? []);
    }

    return props.table.columns.filter((c) => keys.has(c.field));
});

function castName(name: string) {
    return name as keyof typeof slots;
}

function reload() {
    router.replace({
        path: route.path,
        query: {
            page: page.value,
            rows: rows.value,
            search: search.value,
            expanded: isExpanded.value ? 1 : undefined,
        },
    });
}

watch([page, rows, isExpanded], reload);
</script>

<template>
    <template v-if="!isExpanded">
        <CustomTable
            v-model:page="page"
            v-model:rows="rows"
            v-model:search="search"
            :columns="tableCols"
            :table="props.table"
            @search="reload"
        >
            <template #headers="{ refresh }">
                <div class="flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-center py-1">
                    <div class="w-auto md:w-3/12 flex flex-row justify-start">
                        <PV.Button
                            v-if="props.expandable"
                            icon="pi pi-window-maximize"
                            severity="secondary"
                            size="small"
                            text
                            @click="() => (isExpanded = true)"
                        />

                        <PV.Button icon="pi pi-refresh" severity="secondary" size="small" text @click="refresh" />

                        <slot name="_tools"></slot>
                    </div>

                    <div class="grow w-full md:w-auto">
                        <PV.IconField class="grow">
                            <PV.InputIcon class="pi pi-search" />
                            <PV.InputText v-model.trim="search" fluid placeholder="Cari" size="small" />
                            <PV.InputIcon
                                v-if="search !== undefined && search.trim().length > 0"
                                class="pi pi-times hover:cursor-pointer"
                                @click="() => (search = undefined)"
                            />
                        </PV.IconField>
                    </div>

                    <div class="w-auto xl:w-3/12 flex flex-row justify-end gap-1">
                        <slot name="_buttons"></slot>
                    </div>
                </div>
            </template>

            <template v-for="column in tableCols">
                <PV.Column class="whitespace-nowrap" :field="column.field" :header="column.title">
                    <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                        <slot :data="data" :name="castName(column.field)" />
                    </template>
                </PV.Column>
            </template>
        </CustomTable>
    </template>
    <template v-else>
        <PV.Drawer v-model:visible="isExpanded" position="full">
            <template #header>
                <PV.IconField class="w-1/3">
                    <PV.InputIcon class="pi pi-search" />
                    <PV.InputText v-model.trim="search" fluid placeholder="Cari" size="small" />
                    <PV.InputIcon
                        v-if="search !== undefined && search.trim().length > 0"
                        class="pi pi-times hover:cursor-pointer"
                        @click="() => (search = undefined)"
                    />
                </PV.IconField>
            </template>

            <CustomTable
                v-model:page="page"
                v-model:rows="rows"
                v-model:search="search"
                :columns="tableCols"
                :table="props.table"
                @search="reload"
            >
                <template v-for="column in tableCols">
                    <PV.Column class="whitespace-nowrap" :field="column.field" :header="column.title">
                        <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                            <slot :data="data" :name="castName(column.field)" />
                        </template>
                    </PV.Column>
                </template>
            </CustomTable>
        </PV.Drawer>
    </template>
</template>
